import React, { useEffect, useState } from "react";
import api from "../../api/axiosAuth";

import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line
} from "recharts";

export default function InformesRector() {

  const [dashboard, setDashboard] = useState({});
  const [sedesData, setSedesData] = useState([]);
  const [sedesGrafica, setSedesGrafica] = useState([]);
  const [tendencia, setTendencia] = useState([]);
  const [riesgo, setRiesgo] = useState([]);
  const [docentes, setDocentes] = useState([]);

  // 👨‍🎓 estudiantes
  const [verEstudiantes, setVerEstudiantes] = useState(false);
  const [listaEstudiantes, setListaEstudiantes] = useState([]);

  // filtros estudiantes
  const [grado, setGrado] = useState("");
  const [grupo, setGrupo] = useState("");
  const [busqueda, setBusqueda] = useState("");

  const [sedes, setSedes] = useState("ALL");
  const [loading, setLoading] = useState(false);

  /* =====================================================
     🚀 INIT
  ===================================================== */
  useEffect(() => {
    cargarSedes();
  }, []);

  useEffect(() => {
    cargarTodo();
  }, [sedes]);

  /* =====================================================
     📥 SEDES
  ===================================================== */
  const cargarSedes = async () => {
    try {
      const res = await api.get("/sedes");
      setSedesData(res.data);
    } catch (error) {
      console.error("❌ Error cargando sedes:", error);
    }
  };

  /* =====================================================
     📊 DASHBOARD
  ===================================================== */
  const cargarTodo = async () => {
    setLoading(true);

    try {
      const params = `?sedes=${sedes}`;

      const results = await Promise.allSettled([
        api.get(`/informes/rector/dashboard${params}`),
        api.get(`/informes/rector/grafica-cursos${params}`),
        api.get(`/informes/rector/tendencia${params}`),
        api.get(`/informes/rector/riesgo${params}`),
        api.get(`/informes/rector/docentes${params}`)
      ]);

      if (results[0].status === "fulfilled") setDashboard(results[0].value.data);
      if (results[1].status === "fulfilled") setSedesGrafica(results[1].value.data);
      if (results[2].status === "fulfilled") setTendencia(results[2].value.data);
      if (results[3].status === "fulfilled") setRiesgo(results[3].value.data);
      if (results[4].status === "fulfilled") setDocentes(results[4].value.data);

    } catch (error) {
      console.error("❌ Error general:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     👨‍🎓 ESTUDIANTES
  ===================================================== */
  const cargarEstudiantes = async () => {
    try {
      let query = `/informes/rector/estudiantes?sedes=${sedes}`;

      if (grado) query += `&grado=${grado}`;
      if (grupo) query += `&grupo=${grupo}`;
      if (busqueda) query += `&busqueda=${busqueda}`;

      const res = await api.get(query);

      setListaEstudiantes(res.data);
      setVerEstudiantes(true);

    } catch (error) {
      console.error("❌ Error estudiantes:", error);
    }
  };

  /* =====================================================
     📄 PDF OFICIAL
  ===================================================== */
  const exportarPDF = async () => {
    try {
      const res = await api.get("/informes/rector/exportar", {
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "reporte_oficial_estudiantes.pdf");

      document.body.appendChild(link);
      link.click();

    } catch (error) {
      console.error("❌ Error PDF:", error);
    }
  };

  return (
    <div style={{ padding: 20 }}>

      <h2>📊 Informes del Rector</h2>

      {/* 🔎 FILTRO */}
      <div style={{ marginBottom: 20 }}>
        <select value={sedes} onChange={(e) => setSedes(e.target.value)}>
          <option value="ALL">🌐 Todas las sedes</option>
          {sedesData.map((s) => (
            <option key={s.id} value={s.id}>{s.nombre}</option>
          ))}
        </select>

        <button onClick={cargarTodo} style={{ marginLeft: 10 }}>
          Filtrar
        </button>
      </div>

      {loading && <p>⏳ Cargando...</p>}

      {/* 📊 KPIs + ACCIONES */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <div className="card">👨‍🎓 {dashboard?.estudiantes || 0}</div>
        <div className="card">📈 {dashboard?.promedio || 0}</div>
        <div className="card">⚠️ {dashboard?.riesgo || 0}</div>

        <button onClick={cargarEstudiantes}>
          Ver estudiantes
        </button>

        <button onClick={exportarPDF}>
          📄 PDF Oficial
        </button>
      </div>

      {/* 📊 GRAFICA */}
      <h3>🏫 Rendimiento por Sede</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={sedesGrafica}>
          <XAxis dataKey="sede" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="promedio" />
        </BarChart>
      </ResponsiveContainer>

      {/* 📈 TENDENCIA */}
      <h3>📈 Tendencia Académica</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={tendencia}>
          <XAxis dataKey="fecha" />
          <YAxis />
          <Tooltip />
          <Line dataKey="promedio" />
        </LineChart>
      </ResponsiveContainer>

      {/* ⚠️ RIESGO */}
      <h3>⚠️ Estudiantes en Riesgo</h3>
      {riesgo.length === 0 && <p>No hay estudiantes en riesgo</p>}
      {riesgo.map((r, i) => (
        <div key={i}>
          {r.nombre} - Prom: {r.promedio} - Faltas: {r.faltas}
        </div>
      ))}

      {/* 👨‍🏫 DOCENTES */}
      <h3>👨‍🏫 Docentes</h3>
      {docentes.length === 0 && <p>No hay datos</p>}
      {docentes.map((d, i) => (
        <div key={i}>
          {d.nombre || "Docente"} - {d.rendimiento}
        </div>
      ))}

      {/* 👨‍🎓 ESTUDIANTES */}
      {verEstudiantes && (
        <div style={{ marginTop: 40 }}>

          <h3>👨‍🎓 Lista de Estudiantes</h3>

          {/* FILTROS */}
          <div style={{ marginBottom: 10 }}>
            <input
              placeholder="Grado"
              value={grado}
              onChange={(e) => setGrado(e.target.value)}
            />

            <input
              placeholder="Grupo"
              value={grupo}
              onChange={(e) => setGrupo(e.target.value)}
            />

            <input
              placeholder="Buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />

            <button onClick={cargarEstudiantes}>
              Aplicar filtros
            </button>
          </div>

          {/* TABLA */}
          <table border="1" width="100%">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Documento</th>
                <th>Grado</th>
                <th>Grupo</th>
                <th>Sede</th>
              </tr>
            </thead>
            <tbody>
              {listaEstudiantes.map((e) => (
                <tr key={e.id}>
                  <td>{e.nombre}</td>
                  <td>{e.documento}</td>
                  <td>{e.grado}</td>
                  <td>{e.grupo}</td>
                  <td>{e.sede}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      )}

    </div>
  );
}
